import { upperFirst } from "lodash";
import React, { useState } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { Skeleton } from "@/Components/v2/atoms/Skeleton";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { useAnalytics } from "@/hooks/useAnalytics";
import { getLikelihood } from "@/pages/scenario-builder/types";
import { Scenario } from "@/pages/scenario-builder/v3/api/getScenario";

import { useAnalyseSpendAllocationStrategy } from "@/pages/scenario-builder/v3/api/v2/analyseSpendAllocationStrategy";
import { BCPNode } from "@/pages/scenario-builder/v3/utils/bcp";
import HistoricSpendChangeModalPage from "./HistoricSpendChangeModal";

type HistoricSpendChangeProps = {
  scenario: Scenario | undefined;
  distinctBCP?: Record<string, BCPNode>;
};

const HistoricSpendChange: React.FC<HistoricSpendChangeProps> = ({ scenario, distinctBCP }) => {
  const { buttonClicked } = useAnalytics();
  const [percentChangeOpen, setPercentChangeOpen] = useState(false);

  const { data: spendAllocationStrategy, isLoading } = useAnalyseSpendAllocationStrategy({
    scenario: scenario ?? undefined,
  });

  if (isLoading) {
    return (
      <Box sx={{ backgroundColor: "#EEEFFE", borderRadius: 2, p: 2, height: 40 }}>
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Box>
    );
  }

  if (!spendAllocationStrategy || !spendAllocationStrategy.strategyProfile) return null;

  const likelihoodMapping = getLikelihood(spendAllocationStrategy.strategyProfile);

  if (!likelihoodMapping) return null;

  const { label, description } = likelihoodMapping;

  const handleClick = () => {
    // TODO: confirm on these analytics events
    buttonClicked({
      buttonName: "Percent Change from Historic Spend",
      buttonLocation: "Growth Center - Scenario Builder Card",
      buttonType: "modal",
    });

    setPercentChangeOpen(true);
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        p={2}
        borderRadius={2}
        sx={{ backgroundColor: "#EEEFFE", cursor: "pointer" }}
        onClick={handleClick}
      >
        <Box px={2} py={1} border="1px solid #565EFA" borderRadius={1}>
          <Typography variant="body2" sx={{ color: "black" }}>
            {upperFirst(label)} Change from Historic Spend
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ flex: 1, color: "black" }}>
          Given this {label} change from historic spending, returns {description}.
        </Typography>
      </Stack>
      <HistoricSpendChangeModalPage
        open={percentChangeOpen}
        handleClose={() => setPercentChangeOpen(false)}
        allocationStrategy={spendAllocationStrategy}
        distinctBCP={distinctBCP}
      />
    </>
  );
};

export default HistoricSpendChange;
